import { useQuery } from "@tanstack/react-query";
import { useGetIdentity } from "ra-core";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { mxAt, mxMonday, mxYmd } from "../mxDate";

/** Lunes (hora de México) de la semana de una fecha, como aaaa-mm-dd. */
export const mondayOf = (date: Date) => mxMonday(date);

/**
 * Racha: semanas seguidas cumpliendo la meta. La semana en curso cuenta si ya
 * se cumplió; si no, la racha se mide desde la semana pasada.
 */
export const streakOf = (
  demosByWeek: Record<string, number>,
  goal: number,
  today = new Date(),
) => {
  let streak = 0;
  const d = new Date(today);
  if ((demosByWeek[mondayOf(d)] ?? 0) < goal) d.setDate(d.getDate() - 7);
  while ((demosByWeek[mondayOf(d)] ?? 0) >= goal && goal > 0) {
    streak++;
    d.setDate(d.getDate() - 7);
  }
  return streak;
};

/** Meta semanal de demos (crm_settings) y demos "Demo agendada" por semana. */
export const useWeeklyGoal = () => {
  const { identity } = useGetIdentity();
  return useQuery({
    queryKey: ["weekly_goal", identity?.id],
    enabled: !!identity,
    queryFn: async () => {
      const sb = getSupabaseClient();
      const since = new Date();
      since.setDate(since.getDate() - 7 * 60); // hasta ~1 año de racha
      const [{ data: settings }, { data: notes, error }] = await Promise.all([
        sb.from("crm_settings").select("weekly_goal").eq("id", 1).maybeSingle(),
        sb
          .from("contact_notes")
          .select("date")
          .eq("outcome", "demo_agendada")
          .eq("sales_id", identity!.id)
          .gte("date", since.toISOString()),
      ]);
      if (error) throw error;
      const goal = settings?.weekly_goal ?? 5;
      const byWeek: Record<string, number> = {};
      for (const n of notes ?? []) {
        const w = mondayOf(new Date(n.date));
        byWeek[w] = (byWeek[w] ?? 0) + 1;
      }
      return {
        goal,
        thisWeek: byWeek[mondayOf(new Date())] ?? 0,
        streak: streakOf(byWeek, goal),
      };
    },
  });
};

/** Tareas marcadas como hechas hoy por el usuario actual. */
export const useDoneToday = () => {
  const { identity } = useGetIdentity();
  return useQuery({
    queryKey: ["done_today", identity?.id],
    enabled: !!identity,
    queryFn: async () => {
      const start = mxAt(mxYmd()); // 00:00 de hoy en México
      const { count } = await getSupabaseClient()
        .from("tasks")
        .select("id", { count: "exact", head: true })
        .eq("sales_id", identity!.id)
        .gte("done_date", start.toISOString());
      return count ?? 0;
    },
  });
};
